import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import cx from 'classnames';
import marked from 'marked';
import get from 'utils/get';
import slugify from 'utils/slugify';
import flattenMediaData from 'utils/flattenMediaData';
import contentfulImgUtil from 'utils/contentfulImgUtil';
import addLinkAndBoldToTopicWord from 'utils/addLinkAndBoldToTopicWord';

import withLines from 'lib/withLines';
import withBreakpoint from 'lib/withBreakpoint';
import captionImageBlockModel from 'models/captionImageBlockModel';
import { Orientations } from 'constants/Variables';
import CaptionImageBlock from 'components/CaptionImageBlock';
import { Image, ImageCarousel } from 'components/base';

class BlockImageText extends PureComponent {
  state = {
    imagesLoaded: false
  };

  imagesToLoad = [];

  imageMounted = key => {
    this.imagesToLoad.push({ key, loaded: false });
  };

  imageLoaded = key => {
    if (this.imagesToLoad[key]) {
      this.imagesToLoad[key].loaded = true;
    }
    if (this.imagesToLoad.every(image => image.loaded)) {
      this.setState({ imagesLoaded: true });
    }
  };

  render() {
    const {
      block,
      breakpointData,
      setRef,
      backgroundLineKeywords
    } = this.props;

    const fields = get(block, 'fields', {});
    const title = get(fields, 'title', '');
    const image1 = get(fields, 'image1.fields', {});
    const image1Image = flattenMediaData(get(image1, 'image', {}));
    const image1PositionX = get(fields, 'image1PositionX', 0);
    const image1PositionY = get(fields, 'image1PositionY', 0);
    const image2 = get(fields, 'image2.fields', {});
    const image2Image = flattenMediaData(get(image2, 'image', {}));
    const image2PositionX = get(fields, 'image2PositionX', 0);
    const image2PositionY = get(fields, 'image2PositionY', 0);
    const image3 = get(fields, 'image3.fields', {});
    const image3Image = flattenMediaData(get(image3, 'image', {}));
    const image3PositionX = get(fields, 'image3PositionX', 0);
    const image3PositionY = get(fields, 'image3PositionY', 0);
    const textPositionY = get(fields, 'textPositionY', 0);
    const textPositionX = get(fields, 'textPositionX', 0);
    const imageTextRatio = get(fields, 'imageTextRatio', '5:5')
      .split(':')
      .join('-');
    const isReverseArrangement = get(fields, 'isReverseArrangement', false);
    const text = get(fields, 'text', '');
    const textStyle = slugify(get(fields, 'textStyle', 'Medium'));
    const desktopPaddingTop = get(fields, 'desktopPaddingTop', 0);
    const desktopPaddingBottom = get(fields, 'desktopPaddingBottom', 0);
    const mobilePaddingTop = slugify(get(fields, 'mobilePaddingTop', 'Large'));
    const mobilePaddingBottom = slugify(
      get(fields, 'mobilePaddingBottom', 'Large')
    );
    const imageAlign = slugify(get(fields, 'imageAlign', 'Horizontal'));
    const showGuideline = get(fields, 'showGuideline', false);
    const showBlockTitle = get(fields, 'showBlockTitle', false);
    const blockHasMultipleImages =
      !!image1Image.url && (!!image2Image.url || !!image3Image.url);

    const image1Size = slugify(get(image1, 'imageSize', 'none'));
    const image2Size = slugify(get(image2, 'imageSize', 'none'));
    const image3Size = slugify(get(image3, 'imageSize', 'none'));
    const image1SizeByHeight = get(image1, 'imageSizeByHeight', '');
    const image2SizeByHeight = get(image2, 'imageSizeByHeight', '');
    const image3SizeByHeight = get(image3, 'imageSizeByHeight', '');
    const image1TextPosition = slugify(get(image1, 'textPosition', ''));
    const image2TextPosition = slugify(get(image2, 'textPosition', ''));
    const image3TextPosition = slugify(get(image3, 'textPosition', ''));
    const image1Text = get(image1, 'text', '');
    const image2Text = get(image2, 'text', '');
    const image3Text = get(image3, 'text', '');

    const renderer = new marked.Renderer();
    renderer.strong = (text, level) => {
      return `<strong class='connected-with-lines nowrap'>${text}</strong>`;
    };

    renderer.codespan = (text, level) => {
      return `<a href='explore' class='linked-to-explore'>${text}</a>`;
    };

    return (
      <div
        ref={setRef}
        style={
          breakpointData.breakpointIsLarge ||
          breakpointData.breakpointIsExtraLarge
            ? {
                paddingTop: `${desktopPaddingTop}rem`,
                paddingBottom: `${desktopPaddingBottom}rem`
              }
            : {}
        }
        className={cx('BlockImageText relative block-width mxauto', {
          [`padding-top-${mobilePaddingTop} padding-bottom-${mobilePaddingBottom}`]: !breakpointData.breakpointIsLarge,
          guideline: showBlockTitle
        })}
      >
        {showBlockTitle ? (
          <p className="block-title absolute r0 t0">{title}</p>
        ) : null}
        <div
          className={cx(
            `BlockImageText__container relative mxauto BlockImageText__ratio-${imageTextRatio} 
        flex flex-col items-center lg:items-stretch justify-center`,
            {
              'lg:flex-row-reverse': isReverseArrangement,
              'lg:flex-row': !isReverseArrangement
            }
          )}
        >
          <div
            className={cx(
              'BlockImageText__image-container w100 mb5 lg:mb0 flex justify-center',
              {
                guideline: showGuideline,
                'flex-row items-start': imageAlign === Orientations.HORIZONTAL,
                'flex-col': imageAlign === Orientations.VERTICAL,
                'items-center': imageAlign !== Orientations.HORIZONTAL
              }
            )}
          >
            {blockHasMultipleImages ? (
              <ImageCarousel
                className="mobile-carousel"
                imagesLoaded={this.state.imagesLoaded}
                waitForImagesToLoad
              >
                {[image1, image2, image3].map((imageBlock, i) => {
                  const image = flattenMediaData(get(imageBlock, 'image', {}));
                  const textAlign = slugify(
                    get(imageBlock, 'textAlign', 'Start')
                  );

                  if (!!image.url) {
                    const text = get(imageBlock, 'text', '');
                    const textWidthIsSmall = get(
                      imageBlock,
                      'textWidthSmall',
                      false
                    );

                    return (
                      <div
                        className={cx('carousel-container flex flex-col', {
                          'items-start': textAlign === 'start',
                          'items-center': textAlign === 'center',
                          'items-end': textAlign === 'end'
                        })}
                        key={get(image, 'title', i)}
                      >
                        <div className="w100 px_5 md:px1">
                          <Image
                            onMount={() => this.imageMounted(i)}
                            onImgLoad={() => this.imageLoaded(i)}
                            className="w100 fit-contain"
                            src={contentfulImgUtil(
                              get(image, 'url', ''),
                              '1024'
                            )}
                            alt={get(image, 'description', '')}
                          />
                        </div>
                        <div
                          className={cx(
                            'caption caption-text-width markdown-text p1 z-line-text',
                            {
                              'caption-text-width-small': textWidthIsSmall
                            }
                          )}
                          dangerouslySetInnerHTML={{
                            __html: marked(text)
                          }}
                        />
                      </div>
                    );
                  }
                  return null;
                })}
              </ImageCarousel>
            ) : null}
            {image1Image.url ? (
              <div
                style={
                  breakpointData.breakpointIsLarge ||
                  breakpointData.breakpointIsExtraLarge
                    ? {
                        transform: `translate(${image1PositionX}%, ${image1PositionY}%)`
                      }
                    : {}
                }
                className={cx(`image-width-${image1Size}`, {
                  'none lg:block z1': blockHasMultipleImages,
                  [`image-width-${image1Size}-container`]:
                    !image1SizeByHeight &&
                    image1TextPosition !== 'bottom' &&
                    image1Text
                })}
              >
                <CaptionImageBlock block={image1} />
              </div>
            ) : null}
            {!!image2Image.url ? (
              <div
                style={{
                  transform: `translate(${image2PositionX}%, ${image2PositionY}%)`
                }}
                className={cx(`z2 none lg:block image-width-${image2Size}`, {
                  [`image-width-${image2Size}-container`]:
                    !image2SizeByHeight &&
                    image2TextPosition !== 'bottom' &&
                    image2Text
                })}
              >
                <CaptionImageBlock block={image2} />
              </div>
            ) : null}
            {!!image3Image.url && (
              <div
                style={{
                  transform: `translate(${image3PositionX}%, ${image3PositionY}%)`
                }}
                className={cx(
                  `absolute w100 h100 z3 none lg:flex items-center justify-center image-width-${image3Size}`,
                  {
                    [`image-width-${image3Size}-container`]:
                      !image3SizeByHeight &&
                      image3TextPosition !== 'bottom' &&
                      image3Text
                  }
                )}
              >
                <CaptionImageBlock block={image3} />
              </div>
            )}
          </div>
          <div
            className={cx(
              'BlockImageText__text-container z2 w100 lg:px1 px2_5 lg:px0 flex flex-col items-center justify-center z-line-text events-none',
              { guideline: showGuideline }
            )}
          >
            <div
              style={
                breakpointData.breakpointIsLarge ||
                breakpointData.breakpointIsExtraLarge
                  ? {
                      transform: `translate(${textPositionX}%, ${textPositionY}%)`
                    }
                  : {}
              }
              className={cx(
                'BlockImageText__text markdown-text events-all w100 z-line-text',
                {
                  'BlockImageText__small-text story-description-small':
                    textStyle === 'small',
                  'story-description': textStyle !== 'small'
                }
              )}
              dangerouslySetInnerHTML={{
                __html: marked(
                  addLinkAndBoldToTopicWord(text, backgroundLineKeywords),
                  { renderer }
                )
              }}
            />
          </div>
        </div>
      </div>
    );
  }
}

BlockImageText.propTypes = {
  block: PropTypes.shape({
    fields: PropTypes.shape({
      title: PropTypes.string,
      image1: PropTypes.shape({
        fields: captionImageBlockModel.propTypes
      }),
      image1PositionX: PropTypes.number,
      image1PositionY: PropTypes.number,
      image2: PropTypes.shape({
        fields: captionImageBlockModel.propTypes
      }),
      image2PositionX: PropTypes.number,
      image2PositionY: PropTypes.number,
      image3: PropTypes.shape({
        fields: captionImageBlockModel.propTypes
      }),
      image3PositionX: PropTypes.number,
      image3PositionY: PropTypes.number,
      textPositionY: PropTypes.number,
      textPositionX: PropTypes.number,
      imageTextRatio: PropTypes.string,
      isReverseArrangement: PropTypes.bool,
      textStyle: PropTypes.string,
      desktopPaddingTop: PropTypes.number,
      desktopPaddingBottom: PropTypes.number,
      mobilePaddingTop: PropTypes.string,
      mobilePaddingBottom: PropTypes.string,
      imageAlign: PropTypes.string,
      showGuideline: PropTypes.bool,
      showBlockTitle: PropTypes.bool
    })
  })
};

BlockImageText.defaultProps = {
  block: {
    fields: {
      title: '',
      image1: {
        fields: captionImageBlockModel.default
      },
      image1PositionX: 0,
      image1PositionY: 0,
      image2: {
        fields: captionImageBlockModel.default
      },
      image2PositionX: 0,
      image2PositionY: 0,
      image3: {
        fields: captionImageBlockModel.default
      },
      image3PositionX: 0,
      image3PositionY: 0,
      textPositionY: 0,
      textPositionX: 0,
      imageTextRatio: '5:5',
      isReverseArrangement: false,
      textStyle: 'Medium',
      desktopPaddingTop: 0,
      desktopPaddingBottom: 0,
      mobilePaddingTop: 'Large',
      mobilePaddingBottom: 'Large',
      imageAlign: 'Horizontal',
      showGuideline: false,
      showBlockTitle: false
    }
  }
};

export default withBreakpoint(withLines(BlockImageText));
